/**
 * Monta o contexto de avaliação de um formulário em preenchimento e devolve quais campos ficam
 * visíveis — numa passada única por `(ordem, id)`, como o processador do backend.
 *
 * Campo com condição falsa fica oculto e **não** entra no contexto: quem depende dele também some
 * (cascata). O resultado é só UX; o backend recalcula no envio.
 */
import { Condicao, ContextoCondicao, ValorContexto } from './tipos'
import { avaliarCondicao } from './avaliarCondicao'
import { CampoFormularioBase, ordenarCampos, tipoCampoCondicaoDe } from './esquema'
import { numeroDeTexto, valorDeDesconhecido, valorNumero, valorTexto, valorTextos } from './valores'

export type ResultadoContexto = {
  contexto: ContextoCondicao
  /** Códigos dos campos visíveis, na ordem de preenchimento. */
  visiveis: string[]
}

const valorDaResposta = (campo: CampoFormularioBase, bruto: unknown): ValorContexto | null => {
  const tipo = tipoCampoCondicaoDe(campo.tipo)
  if (tipo === 'NAO_SUPORTADO' || bruto == null) return null

  if (tipo === 'NUMERO') {
    if (typeof bruto === 'number') return Number.isFinite(bruto) ? valorNumero(bruto) : null
    const numero = numeroDeTexto(String(bruto))
    return numero == null ? null : valorNumero(numero)
  }
  if (campo.multiplo) {
    return valorTextos(Array.isArray(bruto) ? bruto.map((item) => String(item)) : [String(bruto)])
  }
  if (Array.isArray(bruto)) {
    return bruto.length > 0 ? valorTexto(String(bruto[0])) : null
  }
  return valorTexto(String(bruto))
}

/** Converte o mapa de variáveis resolvidas (vindo do backend ou da simulação) para o contexto. */
export const contextoDeVariaveis = (
  variaveis: Record<string, unknown> | null | undefined
): Record<string, ValorContexto> => {
  const mapa: Record<string, ValorContexto> = {}
  Object.entries(variaveis ?? {}).forEach(([codigo, bruto]) => {
    const valor = valorDeDesconhecido(bruto)
    if (valor) mapa[codigo] = valor
  })
  return mapa
}

export const montarContexto = (
  campos: CampoFormularioBase[],
  valores: Record<string, unknown>,
  variaveis?: Record<string, unknown> | null,
  sinais?: Record<string, number> | null
): ResultadoContexto => {
  const contexto: ContextoCondicao = {
    respostas: {},
    variaveis: contextoDeVariaveis(variaveis),
    sinais: { ...(sinais ?? {}) },
  }
  const visiveis: string[] = []

  ordenarCampos(campos).forEach((campo) => {
    const codigo = campo.codigo
    if (!codigo) return
    if (campo.condicaoExibicao && !avaliarCondicao(campo.condicaoExibicao as Condicao, contexto)) {
      return
    }
    visiveis.push(codigo)
    const valor = valorDaResposta(campo, valores[codigo])
    if (valor) contexto.respostas[codigo] = valor
  })

  return { contexto, visiveis }
}
